import { useTranslation } from "react-i18next";
import { createStyles } from "antd-style";

import { Card } from "@av/components/atoms/Card";
import { Checkbox } from "@av/components/atoms/Checkbox";
import { Form } from "@av/components/atoms/form/Form";
import { Typography } from "@av/components/atoms/typography/Typography";

const useStyles = createStyles(({ css, token }) => ({
  // Purple accent line down the left edge of the consent section (Figma default state).
  // Turns red when the required-consent rule fails: antd marks the inner Form.Item with
  // .ant-form-item-has-error on submit, and :has() lets the card react to that without a
  // form-instance subscription (the app targets modern Chromium).
  consentCard: css`
    border-left: 2px solid ${token.colorPrimaryBorderHover};
    padding-left: ${token.paddingLG}px;

    &:has(.ant-form-item-has-error) {
      border-left-color: ${token.colorError};
    }
  `,
  disclaimer: css`
    display: block;
    margin-bottom: ${token.marginMD}px;
  `,
  formItem: css`
    margin-bottom: 0;
  `,
  // "I agree" label stays purple in both the default and error states (Figma).
  consentCheckbox: css`
    color: ${token.colorPrimary};
  `,
}));

/**
 * Consent acknowledgement for the public application form (HR-3274).
 *
 * Rendered inside the application Form so the required-consent rule participates in
 * the same submit validation as the other fields. Mounted only when the
 * `ats_candidate_consent` flag is on, so this card never renders while the feature is dark.
 */
export function ConsentCard() {
  const { t } = useTranslation();
  const { styles } = useStyles();

  return (
    <Card
      title={t("recruitment.jobApplication.section_consent")}
      testIdPrefix="candidate-application-consent"
      className={styles.consentCard}
    >
      <Typography.Text
        className={styles.disclaimer}
        testIdPrefix="candidate-application-consent-disclaimer"
      >
        {t("recruitment.jobApplication.consentDisclaimer")}
      </Typography.Text>

      <Form.Item
        name="dataSharingConsentGiven"
        valuePropName="checked"
        // labelString provides the accessible name for the field (no visible label is
        // rendered here); the Checkbox child below is the visible text. Both intentionally
        // use the same approved label_consent string.
        labelString={t("recruitment.jobApplication.label_consent")}
        className={styles.formItem}
        testIdPrefix="candidate-application-consent"
        rules={[
          {
            validator: (_rule, value) =>
              value === true
                ? Promise.resolve()
                : Promise.reject(new Error(t("recruitment.jobApplication.consentRequiredError"))),
          },
        ]}
      >
        <Checkbox testIdPrefix="candidate-application-consent" className={styles.consentCheckbox}>
          {t("recruitment.jobApplication.label_consent")}
        </Checkbox>
      </Form.Item>
    </Card>
  );
}
